import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { listAdminUsersApi, AdminUserItem } from "../../features/users/api/profile.api"
import { Avatar } from "../../shared/ui/Avatar"
import { ChevronRightIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, UsersIcon } from "@heroicons/react/16/solid"

const LIMIT = 15

const formatDate = (unixSeconds: number) =>
  new Date(unixSeconds * 1000).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" })

export const ReferralUsersPage = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<AdminUserItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const [pendingSearch, setPendingSearch] = useState("")
  const [search, setSearch] = useState("")

  const load = () => {
    setLoading(true)
    setError(false)
    listAdminUsersApi({ limit: LIMIT, offset: (page - 1) * LIMIT, search: search || undefined })
      .then((res) => {
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [page, search]) // eslint-disable-line

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    setSearch(pendingSearch.trim())
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b bg-gray-50/60 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-800">Пользователи</h3>

        <div className="flex items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              placeholder="Поиск по имени или email"
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </form>
          <span className="text-xs text-gray-400 whitespace-nowrap">{total} пользователей</span>
        </div>
      </div>

      {error ? (
        <div className="py-16 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400 mb-2">Не удалось загрузить пользователей</p>
          <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
            Повторить
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                {["Пользователь", "Email", "Роль", "Зарегистрирован", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={5} className="px-4 py-3.5">
                      <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
                    </td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-16 text-center">
                    <UsersIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Пользователи не найдены</p>
                  </td>
                </tr>
              ) : (
                items.map((u) => (
                  <tr key={u.user_id}
                    onClick={() => navigate(`/admin/referrals/users/${u.user_id}`)}
                    className="hover:bg-blue-50/40 transition-colors cursor-pointer group">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={u.full_name || u.email} src={u.avatar} />
                        <span className="font-medium text-gray-800">{u.full_name ?? "Без имени"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3.5 text-gray-500 text-xs capitalize">{u.role}</td>
                    <td className="px-4 py-3.5 text-gray-500 text-xs">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3.5">
                      <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!error && items.length > 0 && (
        <div className="px-5 py-3.5 border-t bg-gray-50/40 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Стр. <span className="font-medium text-gray-600">{page}</span> из{" "}
            <span className="font-medium text-gray-600">{totalPages}</span>
          </span>
          <div className="flex gap-1.5">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              ← Пред.
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              След. →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
